"use client";

import { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

export const simplifiedPrizesInputs = [
  {
    name: "discoverUrlParent",
    friendlyName: "Discover button parent URL",
    type: "string",
    required: false,
  },
  {
    name: "discoverButtonText",
    friendlyName: "Discover button text",
    type: "string",
    required: false,
  },
  {
    name: "earnPointsCtaText",
    friendlyName: "Earn points button text",
    type: "string",
    defaultValue: "Earn {#missingPoints#} to redeem.",
    required: true,
  },
  {
    name: "earnPointsCtaUrl",
    friendlyName: "Earn points button url",
    type: "string",
    required: true,
  },
  {
    name: "costLabel",
    friendlyName: "Cost Label",
    type: "string",
    defaultValue: "Cost",
    required: true,
  },
  {
    name: "availablePointsLabel",
    friendlyName: "Available Points Label",
    type: "string",
    defaultValue: "Available points",
    required: true,
  },
  {
    name: "balanceAfterRedemptionLabel",
    friendlyName: "Balance After Redemption Label",
    type: "string",
    defaultValue: "Balance after redemption",
    required: true,
  },
  {
    name: "primaryCta",
    friendlyName: "Primary CTA",
    type: "string",
    defaultValue: "Confirm",
    required: true,
  },
  {
    name: "secondaryCta",
    friendlyName: "Secondary CTA",
    type: "string",
    defaultValue: "Cancel",
    required: true,
  },
  {
    name: "dialogTitle",
    friendlyName: "Dialog Title",
    type: "string",
    defaultValue: "Confirm your reward",
    required: true,
  },
  {
    name: "dialogTitleConfirmation",
    friendlyName: "Dialog Title Confirmation",
    type: "string",
    defaultValue: "Congratulations",
    required: true,
  },
  {
    name: "bottomText",
    friendlyName: "Bottom Text",
    type: "richText",
    defaultValue:
      '<p>By clicking you agree to our <a href="#">terms and conditions</a></p>\r\n',
    required: true,
  },
  {
    name: "pointsLabel",
    friendlyName: "Points Label",
    type: "string",
    defaultValue: "Points",
    required: true,
  },
  {
    name: "confirmationMessage",
    friendlyName: "Confirmation Message",
    type: "string",
    defaultValue: "{#redeemedPoints#} points redeemed for {#voucherName#}",
    helperText:
      "Uses placeholders for dynamic data such as: {#redeemedPoints#}, {#voucherName#}",
    required: true,
  },
  {
    name: "confirmationMessagePlural",
    friendlyName: "Confirmation Message Plural",
    type: "string",
    defaultValue:
      "{#redeemedPoints#} points redeemed for {#qty#} x {#voucherName#}",
    helperText:
      "Uses placeholders for dynamic data such as: {#redeemedPoints#}, {#voucherName#}, {#qty#}",
    required: true,
  },
  {
    name: "codeLabel",
    friendlyName: "Code Label",
    type: "string",
    defaultValue: "Your code:",
    required: true,
  },
  {
    name: "codeInfo",
    friendlyName: "Code Info",
    type: "richText",
    defaultValue:
      '<p>Your code is stored in <a href="#">points transaction history</a></p>\r\n',
    required: true,
  },
  {
    name: "unitLabel",
    friendlyName: "Unit Label",
    type: "string",
    defaultValue: "Unit",
    required: true,
  },
  {
    name: "emailLabel",
    friendlyName: "Email Label",
    type: "string",
    defaultValue: "Email",
  },
  {
    name: "emailHint",
    friendlyName: "Email Hint",
    type: "string",
    defaultValue: "You will receive a reward redemption confirmation email",
  },
  {
    name: "externalCodeButtonLabel",
    friendlyName: "External Code Button Label",
    type: "string",
    defaultValue: "Get Code",
  },
  {
    name: "ctaLabel",
    friendlyName: "CTA Label",
    type: "string",
    defaultValue: "Redeem Now",
    required: true,
  },
  {
    name: "color",
    friendlyName: "Font color",
    type: "string",
    enum: [
      { label: "Light", value: "white" },
      { label: "Dark", value: "black" },
    ],
    defaultValue: "white",
  },
  {
    name: "noOffersText",
    friendlyName: "No Offers Text",
    type: "string",
    defaultValue: "No offers available.",
    required: true,
  },
  {
    name: "newOfferDays",
    friendlyName: "New Offer Days",
    helperText: "How many days old prize is considered new",
    type: "number",
    defaultValue: 7,
  },
  {
    name: "newLabel",
    friendlyName: "New Label",
    type: "string",
    defaultValue: "New!",
    required: true,
  },
  {
    name: "oneDayLabel",
    friendlyName: "Last day label",
    type: "string",
    defaultValue: "Last day!",
  },
  {
    name: "daysLabel",
    friendlyName: "Days Label",
    type: "string",
    defaultValue: "{#days#} days left",
    helperText: "Uses placeholders for dynamic data such as: {#days#}",
  },
  {
    name: "pagination",
    friendlyName: "Pagination",
    type: "number",
    defaultValue: 3,
    required: true,
  },
  {
    name: "isTeaser",
    friendlyName: "Is Teaser",
    type: "boolean",
    defaultValue: true,
    required: true,
  },
  {
    name: "teaserData",
    friendlyName: "Teaser Data",
    type: "object",
    showIf: 'options.get("isTeaser") === true',
    subFields: [
      {
        name: "teaserTitle",
        friendlyName: "Teaser Title",
        type: "string",
        required: true,
      },
      {
        name: "teaserLinkUrl",
        friendlyName: "Teaser Link URL",
        type: "url",
        required: true,
      },
      {
        name: "teaserLinkText",
        friendlyName: "Teaser Link Text",
        type: "string",
        required: true,
      },
    ],
    defaultValue: {
      teaserTitle: "LATEST ADDITION TO REWARD’S CATALOGUE",
      teaserLinkUrl: "#",
      teaserLinkText: "View all rewards",
    },
    required: true,
  },
  {
    name: "loadMoreButtonLabel",
    friendlyName: "Load More Button Label",
    type: "string",
    defaultValue: "Load More",
    required: true,
  },
  {
    name: "errorMessage",
    friendlyName: "Error Message",
    type: "string",
    defaultValue: "Something went wrong, please try again later",
  },
  {
    name: "outOfStockLabel",
    friendlyName: "Out of Stock Label",
    type: "string",
    defaultValue: "Out of Stock",
    required: true,
  },
] as const satisfies Inputs;

export const SimplifiedPrizesComponent: RegisteredComponent = {
  name: "SimplifiedPrizes",
  component: dynamic(async () =>
    withValidation(
      simplifiedPrizesInputs,
      (await import("./SimplifiedPrizes")).SimplifiedPrizes,
      "SimplifiedPrizes"
    )
  ),
  inputs: simplifiedPrizesInputs,
};
